import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaShoppingCart } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import ItensPedidoComponent from './ItensPedidoComponent';
import '../../styles/order-page.css';

const ItensPedidoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Verificar se o usuário é admin
  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Verificar se o usuário é admin na tabela de administradores
          const { data: adminData, error } = await supabase
            .from('caex_administradores')
            .select('*')
            .eq('user_id', user.id)
            .eq('ativo', true)
            .single();
          
          if (!error && adminData) {
            setIsAdmin(true);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar permissões de admin:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminRole();
  }, []);

  // Carregar dados de exemplo ou do estado anterior
  useEffect(() => {
    // Se viermos de outra página com itens no state, use-os
    if (location.state?.items) {
      setCart(location.state.items);
    } else {
      // Carregue dados de exemplo
      const sampleItems = [
        {
          id: 1,
          name: "Ingresso para Expositor",
          description: "Cortesia",
          category: "Credenciais",
          price: 1.00,
          quantity: 10
        }
      ];
      setCart(sampleItems);
    }
  }, [location.state]);

  // Lidar com atualizações nos itens
  const handleUpdateItems = (updatedItems) => {
    setCart(updatedItems);
  };

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calculate cart total
  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      const finalPrice = item.customPrice || item.price;
      const discount = item.discount || 0;
      let itemPrice = finalPrice;
      
      if (discount > 0) {
        if (item.discountType === 'percentage') {
          itemPrice = finalPrice * (1 - discount / 100);
        } else {
          itemPrice = Math.max(0, finalPrice - discount);
        }
      }
      
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Calculate total items
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Continuar para o próximo passo (em um fluxo real, isso salvaria os itens)
  const handleContinue = () => {
    // Em um fluxo real, você pode enviar para o estado da próxima página
    navigate('/confirmar-pedido', { state: { items: cart } });
  };

  if (loading) {
    return (
      <div className="order-page-container">
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page-container">
      <div className="order-page-hub">
        {/* Header */}
        <div className="order-page-header">
          <div className="order-page-title-section">
            <div className="order-page-title-left">
              <button 
                className="order-page-back-button"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft /> Voltar
              </button>
              <h1 className="order-page-main-title">Itens do Pedido</h1>
              {isAdmin && (
                <Badge bg="warning" className="ms-2">
                  Modo Administrador
                </Badge>
              )}
            </div>
            
            <div className="order-page-cart-summary">
              <FaShoppingCart className="order-page-cart-icon" />
              <span className="order-page-items-count">
                {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'}
              </span>
              <h4 className="order-page-cart-total">{formatCurrency(calculateCartTotal())}</h4>
            </div>
          </div>
        </div>

        <Row>
          {/* Itens do Pedido */}
          <Col lg={12}>
            <ItensPedidoComponent 
              items={cart} 
              onUpdateItems={handleUpdateItems}
            />
          </Col>
        </Row>

        {/* Botões de ação */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-end">
            <Button 
              variant="outline-secondary" 
              className="me-2"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary"
              onClick={handleContinue}
              disabled={cart.length === 0}
            >
              Continuar
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ItensPedidoPage;